import React from 'react'
import DraggableItem from '../../../../components/drag-drop/DraggableItem'
import { MdOutlineDragIndicator, MdOutlinePublishedWithChanges } from 'react-icons/md'
import { classNames } from '../../../../helpers/classNames'
import { useMutation, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { StarIcon } from '@heroicons/react/solid'
import { ChevronDoubleUpIcon, ChevronDoubleDownIcon } from '@heroicons/react/outline'
import { FaTasks } from 'react-icons/fa'
import { API_AUTH_TYPES, API_ROUTES, ApiRequest } from '../../../../api'
import { DRAG_N_DROP_TYPES } from '../../../../constants'
import DropTarget from '../../../../components/drag-drop/DropTarget'
import { ToolTipBottom, ToolTipHoverContainer } from '../../../../components/ToolTips'
import ItemMenu from '../../../../components/menus/ItemMenu'

/**
 * Sidebar list item representing a single chapter in the advisor Financial
 * Story table of contents. Supports drag-and-drop reordering of chapters,
 * and accepts task/goal drops to move them into this chapter. Displays task
 * and goal count badges. Provides "Move to Top" and "Move to Bottom" quick
 * actions via an item menu.
 *
 * @param {object}   props
 * @param {object}   props.chapter               - Chapter data object
 * @param {string}   props.selectedChapterId      - ID of the currently selected chapter
 * @param {Function} props.setSelectedChapterId   - Selects this chapter
 * @param {Array}    props.sections               - All section definitions with their query keys
 * @param {object}   props.queryKeys              - Map of React-Query keys for invalidation
 * @param {string}   props.tasksListQueryKey       - Query key for the tasks list
 */
const ChapterItem = ({ chapter, selectedChapterId, setSelectedChapterId, sections, queryKeys, tasksListQueryKey }) => {
    const { household_id: householdId } = useParams()
    const chaptersApi = new ApiRequest(API_ROUTES.FINANCIAL_STORY_CHAPTERS, API_AUTH_TYPES.ADVISOR);
    const tasksApi = new ApiRequest(API_ROUTES.CLIENT_TASKS, API_AUTH_TYPES.ADVISOR);
    const goalsApi = new ApiRequest(API_ROUTES.HOUSEHOLD_GOALS, API_AUTH_TYPES.ADVISOR)

    const queryClient = useQueryClient();
    const { mutate: updateChapterOrder } = useMutation(
        (body) => chaptersApi.patch({ endpoint: "update_chapter_order", body, params: [householdId] }),
        {
            onSuccess: (body) => {
                setTimeout(() => {
                    queryClient.invalidateQueries(sections.find(section => section.type === body.item_list_id).query_key);
                    queryClient.invalidateQueries(sections.find(section => section.type === body.dropped_over_list_id).query_key);
                }, 0.2 * 1000)


            },
        }

    );


    const handleChapterReorder = (item) => {
        const droppedChapterId = item.content.props.chapter.chapter_id
        updateChapterOrder({
            item_id: droppedChapterId,
            dropped_over_id: chapter.chapter_id,
            item_list_id: item.content.props.chapter.status,
            dropped_over_list_id: chapter.status,
            item_type: DRAG_N_DROP_TYPES.CHAPTER,
            household_id: householdId
        });
    }

    const { mutate: updateTaskChapter } = useMutation(
        (body) =>
            tasksApi.patch({
                endpoint: "update_task_chapter",
                body,
            }),
        {
            onSuccess: ({ old_chapter_status, new_chapter_status }) => {
                queryClient.invalidateQueries(queryKeys[old_chapter_status]
                );
                queryClient.invalidateQueries(queryKeys[new_chapter_status])
                queryClient.invalidateQueries(tasksListQueryKey);
            }
        }
    );
    const { mutate: updateGoalChapter } = useMutation(
        (body) => goalsApi.patch({
            endpoint: "add_to_chapter",
            body,
        }),
        {
            onSuccess: ({ old_chapter_status, new_chapter_status }) => {
                queryClient.invalidateQueries(queryKeys[old_chapter_status]
                );
                queryClient.invalidateQueries(queryKeys[new_chapter_status])
                queryClient.invalidateQueries(queryKeys.goals);
            }
        }
    );



    const { mutate: reorderChapterToTop } = useMutation(
        (body) => chaptersApi.patch({ endpoint: "reorder_to_top", body }),
        {
            onSuccess: (body) => {
                setTimeout(() => {
                    queryClient.invalidateQueries(sections.find(section => section.type === body.item_list_id)?.query_key);
                }, 0.2 * 1000)
            },
        }
    );

    const { mutate: reorderChapterToBottom } = useMutation(
        (body) => chaptersApi.patch({ endpoint: "reorder_to_end", body }),
        {
            onSuccess: (body) => {
                setTimeout(() => {
                    queryClient.invalidateQueries(sections.find(section => section.type === body.item_list_id)?.query_key);
                }, 0.2 * 1000)
            },
        }
    );

    const handleMoveToTop = () => {
        reorderChapterToTop({
            item_id: chapter.chapter_id,
            item_list_id: chapter.status,
            dropped_over_list_id: chapter.status,
            item_type: DRAG_N_DROP_TYPES.CHAPTER,
            household_id: householdId,
        });
    };

    const handleMoveToBottom = () => {
        reorderChapterToBottom({
            item_id: chapter.chapter_id,
            item_list_id: chapter.status,
            dropped_over_list_id: chapter.status,
            item_type: DRAG_N_DROP_TYPES.CHAPTER,
            household_id: householdId,
        });
    };

    const chapterMenuOptions = [
        {
            title: "Move to Top",
            action: handleMoveToTop,
            icon: ChevronDoubleUpIcon,
        },
        {
            title: "Move to Bottom",
            action: handleMoveToBottom,
            icon: ChevronDoubleDownIcon,
        },
    ];

    const handleDropItem = (item) => {
        if (item.type === 'goal') {

            updateGoalChapter({
                item_id: item.content.props.goal.goal_id,
                item_list_id: chapter.chapter_id,
                item_type: DRAG_N_DROP_TYPES.GOAL,
                household_id: householdId
            })
        }
        if (item.type === 'task') {
            updateTaskChapter({
                task_id: item.content.props.task.task_id,
                old_chapter_id: item.content.props.task.chapter_id,
                new_chapter_id: chapter.chapter_id,
                household_id: householdId
            })
        }

    }



    return (
        <DropTarget accept="chapter" onDrop={(item) => handleChapterReorder(item)} className={''} isActiveStyling={'pt-10 drop-target'}>
            <DropTarget
                accept={['task', 'goal']}
                onDrop={(item) => handleDropItem(item)} onIsOver={(item) => { setSelectedChapterId(chapter.chapter_id) }}
                isActiveStyling={'bg-gray-200 rounded-lg animate-pulse'}>
                <button
                    className={classNames(
                        chapter.chapter_id === selectedChapterId ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50',
                        'w-full text-left rounded-lg py-1.5 px-2 text-base flex justify-between items-center'
                    )}
                    onClick={() => { setSelectedChapterId(chapter.chapter_id) }}
                >
                    <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{chapter.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                            {chapter.task_count > 0 && (
                                <span className="text-sm text-blue-500 flex items-center gap-0.5">
                                    <FaTasks className='h-3.5 w-3.5' />
                                    {chapter.task_count}
                                </span>
                            )}
                            {chapter.is_highlighted_task_count > 0 && (
                                <span className="text-sm text-yellow-500 flex items-center gap-0.5">
                                    <StarIcon className='h-3.5 w-3.5' />
                                    {chapter.is_highlighted_task_count}
                                </span>
                            )}
                            {chapter.goal_count > 0 && (
                                <span className="text-sm text-gray-400 flex items-center gap-0.5">
                                    <MdOutlinePublishedWithChanges className='h-3.5 w-3.5' />
                                    {chapter.goal_count}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        <DraggableItem
                            item={{
                                type: 'chapter',
                                content: <ChapterItem chapter={chapter} type="chapter"
                                    selectedChapterId={selectedChapterId} setSelectedChapterId={setSelectedChapterId} />
                            }} type='chapter'>
                            <MdOutlineDragIndicator className="h-4 w-4 text-gray-400" />
                        </DraggableItem>
                        <ItemMenu options={chapterMenuOptions} />
                    </div>
                </button>
            </DropTarget>
        </DropTarget>
    )
}

export default ChapterItem 